import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EntryDto, EntryService, GetEntryResponseDto, PostEntryRequest } from '../../api';
import { Observable } from 'rxjs';
import { environment } from 'src/assets/env/environment';
import { AnnotatedCopyResponse, PhotoGroup } from './photo-layout.models';

/**
 * Entry Service
 *
 * Manages entry-related operations (audit entries).
 * All HTTP errors are automatically handled by HttpErrorInterceptor.
 *
 * Features:
 * - CRUD operations for entries
 * - Type-safe methods
 * - Automatic error handling via interceptor
 */
@Injectable({
  providedIn: 'root'
})
export class MEntryService {
  constructor(
    private readonly entryService: EntryService,
    private readonly http: HttpClient,
  ) {}

  /**
   * DOCU-1137: stores the layout of one photo field (order, groups with comment, hidden in
   * export). Replaces the field's previous layout. Hand-written call — the endpoint is not in
   * the generated client yet (same pattern as MAuditService.reassignOwner).
   *
   * @param entryId - The entry the photo field belongs to
   * @param fieldKey - The photo field's unique key
   * @param groups - All groups of the field in display order
   */
  putPhotoLayout(entryId: string, fieldKey: string, groups: PhotoGroup[]): Observable<void> {
    return this.http.put<void>(
      `${environment.API_BASE_URL}/api/v1/entry/${entryId}/photo-layout/${encodeURIComponent(fieldKey)}`,
      { groups },
    );
  }

  /**
   * DOCU-1143: the image of a photo, loaded through the API (DOCU-1351). The presigned bucket URL
   * cannot be drawn into an exportable canvas — the buckets send no CORS headers.
   */
  getPhotoContent(entryId: string, fileId: string): Observable<Blob> {
    return this.http.get(
      `${environment.API_BASE_URL}/api/v1/entry/${entryId}/photos/${fileId}/content`,
      { responseType: 'blob' },
    );
  }

  /**
   * DOCU-1143: stores an annotated image of photo `fileId` as a copy (DOCU-1142). The original
   * stays and is hidden in the export; annotating again replaces the previous copy.
   */
  postAnnotatedCopy(entryId: string, fileId: string, image: Blob): Observable<AnnotatedCopyResponse> {
    const body = new FormData();
    body.append('file', image, 'markiert.jpg');
    return this.http.post<AnnotatedCopyResponse>(
      `${environment.API_BASE_URL}/api/v1/entry/${entryId}/photos/${fileId}/annotation`,
      body,
    );
  }

  /** DOCU-1143: discards the annotated copy; `fileId` may be the copy or its original. */
  deleteAnnotatedCopy(entryId: string, fileId: string): Observable<AnnotatedCopyResponse> {
    return this.http.delete<AnnotatedCopyResponse>(
      `${environment.API_BASE_URL}/api/v1/entry/${entryId}/photos/${fileId}/annotation`,
    );
  }

  /**
   * Gets a single entry by ID.
   *
   * @param id - The entry ID
   * @returns Observable of the entry response
   */
  getEntryById(id: string): Observable<GetEntryResponseDto> {
    return this.entryService.getEntryEndpoint(id);
  }

  /**
   * Updates an existing entry.
   *
   * @param entry - The entry to update
   * @returns Observable of the updated entry
   */
  updateEntry(entry: EntryDto): Observable<EntryDto> {
    return this.entryService.putEntryEndpoint(entry);
  }

  /**
   * Creates a new entry.
   *
   * @param entry - The entry to create
   * @returns Observable of the created entry response
   */
  createEntry(entry: EntryDto): Observable<GetEntryResponseDto> {
    const req: PostEntryRequest = {
      entry: entry
    };
    return this.entryService.postEntryEndpoint(req);
  }

  /**
   * Deletes an entry by ID.
   *
   * @param entryId - The entry ID to delete
   * @returns Observable of void
   */
  deleteEntry(entryId: string): Observable<void> {
    return this.entryService.deleteEntryEndpoint(entryId);
  }
}
